import { useEffect, useRef } from 'react'
import type { Locale } from '../i18n/locales'
import { Icon } from './icons/Icon'

/**
 * The drawer's language row opens this instead of toggling in place — a list rather than a
 * question, which is why it is a fourth `.popover` sibling rather than a variant of `Popover`,
 * `ConfirmPopover`, or `AlertPopover` (see those files' docblocks for the split).
 *
 * Only `Locale` values (`i18n/locales.ts`) are real — **the catalog behind a language is what makes
 * it selectable, not this list**, and the two must move in the same change. Russian was one of the
 * `SoonBadge` rows until D200 landed `catalogs/ru/`, and Italian was the last one until
 * `catalogs/it/` landed beside it — so **there is no coming-soon list here any more.** The next
 * language to be named before it is finished brings the second list back with its own `SoonBadge`
 * import; that is the "visible but not yet actionable" treatment D87's reserved operating states
 * and Shabbat's unbuilt panel use, and it is a real answer rather than a placeholder. Do not add a
 * row here for a locale whose catalogs are not written: this list is what `LOCALES` is checked
 * against, and a `code` that is not a `Locale` would not typecheck as one anyway.
 *
 * Adding one is `docs/engineering/adding-a-locale.md`, and this file is step 4 of six.
 *
 * **A row moved up here is not selectable until the server accepts the value**: `App`'s
 * `selectLanguage` is pessimistic and adopts only what `PATCH /users/me` echoes, so a locale
 * missing from `SUPPORTED_LOCALES` (`packages/functions/auth/config.ts`) renders a row that fails
 * every tap with `languageErrorSave` and no other symptom.
 *
 * Names are never run through `t()`: each one is written in its own script, exactly as
 * `languageValue` already is in `drawer.json` — a Hebrew reader must see "עברית", not a
 * translation of the English word "Hebrew".
 */
const LANGUAGE_OPTIONS: readonly { readonly locale: Locale; readonly name: string }[] = [
  { locale: 'en', name: 'English' },
  { locale: 'he', name: 'עברית' },
  { locale: 'ru', name: 'Русский' },
  { locale: 'it', name: 'Italiano' },
  { locale: 'uk', name: 'Українська' },
]

export function LanguagePopover({
  title,
  current,
  busy = false,
  error,
  onSelect,
  onCancel,
}: {
  readonly title: string
  readonly current: Locale
  readonly busy?: boolean
  readonly error?: string
  onSelect(locale: Locale): void
  onCancel(): void
}): JSX.Element {
  const firstOptionRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    firstOptionRef.current?.focus()
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  return (
    <div className="popover-backdrop" onClick={onCancel}>
      <div
        className="popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-popover-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="popover-title" id="language-popover-title">
          {title}
        </h2>
        <div className="language-option-list" role="listbox" aria-label={title}>
          {LANGUAGE_OPTIONS.map((option, index) => (
            <button
              key={option.locale}
              ref={index === 0 ? firstOptionRef : undefined}
              type="button"
              className={`language-option${option.locale === current ? ' selected' : ''}`}
              data-locale={option.locale}
              role="option"
              aria-selected={option.locale === current}
              disabled={busy}
              onClick={() => onSelect(option.locale)}
            >
              <span>{option.name}</span>
              {option.locale === current ? <Icon name="check" size={16} /> : null}
            </button>
          ))}
        </div>
        {error === undefined ? null : <p className="popover-error">{error}</p>}
      </div>
    </div>
  )
}
